import { decorators, ComponentChanged, OBSERVER_TYPE, GameObject } from '@combos-fun/engine';
import {
  Renderer3D,
  Renderer3DSystem,
  requireNamedResource,
  tagObject3D,
  textureFromResourceImage,
  Transform3D,
  VisualPoseBridge,
} from '@combos-fun/plugin-renderer-3d';
import {
  PlaneGeometry,
  MeshBasicMaterial,
  Mesh,
  DoubleSide,
} from 'three';
import type { Texture } from 'three';
import SpriteAnimation3D from './component';

interface FrameData {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface SpriteAnimation3DEntry {
  mesh: Mesh;
  texture: Texture;
  frames: FrameData[];
  sheetWidth: number;
  sheetHeight: number;
  currentFrame: number;
  elapsed: number;
  playing: boolean;
  poseBridge: VisualPoseBridge;
}

@decorators.componentObserver({
  SpriteAnimation3D: [
    'resource', 'speed',
    'positionX', 'positionY', 'positionZ',
    'rotationX', 'rotationY', 'rotationZ',
    'scaleX', 'scaleY',
  ],
})
export default class SpriteAnimation3DSystem extends Renderer3D {
  static systemName = 'SpriteAnimation3DSystem';
  name: string = 'SpriteAnimation3DSystem';

  private entries: Map<number, SpriteAnimation3DEntry> = new Map();

  init() {
    const renderer3DSystem = this.game.getSystem(Renderer3DSystem) as Renderer3DSystem;
    renderer3DSystem.rendererManager.register(this);
  }

  componentChanged(changed: ComponentChanged) {
    if (changed.componentName !== 'SpriteAnimation3D') return;

    if (changed.type === OBSERVER_TYPE.ADD) {
      this.handleAdd(changed.gameObject, changed.component as SpriteAnimation3D);
    } else if (changed.type === OBSERVER_TYPE.REMOVE) {
      this.handleRemove(changed.gameObject.id);
    } else {
      this.handleChange(changed);
    }
  }

  rendererUpdate(gameObject: GameObject) {
    const component = gameObject.getComponent(SpriteAnimation3D) as SpriteAnimation3D;
    if (!component) return;

    const entry = this.entries.get(gameObject.id);
    if (!entry) return;

    const transform = gameObject.getComponent(Transform3D) as Transform3D | undefined;
    if (!entry.poseBridge.sync(component, transform)) {
      entry.mesh.position.set(0, 0, 0);
      entry.mesh.rotation.set(0, 0, 0);
      entry.mesh.scale.set(1, 1, 1);
    } else {
      entry.mesh.position.set(component.positionX, component.positionY, component.positionZ);
      entry.mesh.rotation.set(component.rotationX, component.rotationY, component.rotationZ);
      entry.mesh.scale.set(component.scaleX, component.scaleY, 1);
    }

    if (!entry.playing || entry.frames.length === 0) return;

    const delta = this.threeContext.clock.getDelta() * 1000;
    entry.elapsed += delta;

    if (entry.elapsed >= component.speed) {
      entry.elapsed -= component.speed;
      entry.currentFrame = (entry.currentFrame + 1) % entry.frames.length;
      this.applyFrame(entry);
    }
  }

  private async handleAdd(gameObject: GameObject, component: SpriteAnimation3D) {
    if (!component.resource) return;

    const asyncId = this.increaseAsyncId(gameObject.id);

    try {
      const res = await requireNamedResource(component.resource);
      if (!this.validateAsyncId(gameObject.id, asyncId)) return;

      const json = res.data?.json;
      if (!json) {
        throw new Error(
          `Resource "${res.name}" has no json data; use RESOURCE_TYPE.SPRITE_ANIMATION with src.image and src.json`,
        );
      }

      const texture = textureFromResourceImage(res);
      if (!this.validateAsyncId(gameObject.id, asyncId)) {
        texture.dispose();
        return;
      }

      const frames = this.parseFrames(json.frames);
      const sheetWidth = json.meta?.size?.w || texture.image?.width || 1;
      const sheetHeight = json.meta?.size?.h || texture.image?.height || 1;

      const firstFrame = frames[0];
      if (!firstFrame) {
        throw new Error(`Resource "${res.name}" spritesheet has no frames`);
      }
      const aspectRatio = firstFrame.w / firstFrame.h;
      const geometry = new PlaneGeometry(aspectRatio, 1);
      const material = new MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: DoubleSide,
      });
      const mesh = new Mesh(geometry, material);
      tagObject3D(mesh, gameObject.id);
      if (this.threeContext.shadows) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }

      const poseBridge = new VisualPoseBridge();
      const entry: SpriteAnimation3DEntry = {
        mesh,
        texture,
        frames,
        sheetWidth,
        sheetHeight,
        currentFrame: 0,
        elapsed: 0,
        playing: component.autoPlay,
        poseBridge,
      };

      this.applyFrame(entry);

      const transform = gameObject.getComponent(Transform3D) as Transform3D | undefined;
      if (poseBridge.sync(component, transform)) {
        mesh.position.set(component.positionX, component.positionY, component.positionZ);
        mesh.rotation.set(component.rotationX, component.rotationY, component.rotationZ);
        mesh.scale.set(component.scaleX, component.scaleY, 1);
      }

      this.threeContext.attachVisual(gameObject.id, mesh, gameObject);
      this.entries.set(gameObject.id, entry);
    } catch (e) {
      console.error('SpriteAnimation3DSystem: failed to load spritesheet', e);
      throw e;
    }
  }

  private handleChange(changed: ComponentChanged) {
    const component = changed.component as SpriteAnimation3D;
    const changedProp = changed.prop?.prop?.[0];

    if (changedProp === 'resource') {
      this.handleRemove(changed.gameObject.id);
      this.handleAdd(changed.gameObject, component);
    }
  }

  private handleRemove(id: number) {
    this.increaseAsyncId(id);
    const entry = this.entries.get(id);
    if (!entry) return;

    this.threeContext.detachVisual(id, entry.mesh);
    entry.mesh.geometry.dispose();
    (entry.mesh.material as MeshBasicMaterial).dispose();
    entry.texture.dispose();
    this.entries.delete(id);
  }

  private applyFrame(entry: SpriteAnimation3DEntry) {
    const frame = entry.frames[entry.currentFrame];
    const tex = entry.texture;

    tex.offset.set(frame.x / entry.sheetWidth, 1 - (frame.y + frame.h) / entry.sheetHeight);
    tex.repeat.set(frame.w / entry.sheetWidth, frame.h / entry.sheetHeight);
  }

  private parseFrames(framesData: any): FrameData[] {
    if (Array.isArray(framesData)) {
      return framesData.map((f: any) => ({
        x: f.frame.x,
        y: f.frame.y,
        w: f.frame.w,
        h: f.frame.h,
      }));
    }
    return Object.keys(framesData)
      .sort()
      .map((key) => ({
        x: framesData[key].frame.x,
        y: framesData[key].frame.y,
        w: framesData[key].frame.w,
        h: framesData[key].frame.h,
      }));
  }

  onDestroy() {
    for (const [id] of this.entries) {
      this.handleRemove(id);
    }
    this.entries.clear();
  }
}
